
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings, Trophy, Users, TrendingUp, MessageCircle, UserPlus, ArrowLeft, MapPin, Calendar, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useFriends } from '@/hooks/useFriends';
import { useUserTournamentsCount } from '@/hooks/useUserTournamentsCount';
import { useUserFollowersCount } from '@/hooks/useUserFollowersCount';
import { useUserFollowingCount } from '@/hooks/useUserFollowingCount';
import RecentActivity from '@/components/profile/RecentActivity';
import FriendsNetwork from '@/components/profile/FriendsNetwork';
import MyEvents from '@/components/profile/MyEvents';
import { useMyEvents } from '@/hooks/useMyEvents';
import { FollowersListDialog } from '@/components/profile/FollowersListDialog';
import { FollowingListDialog } from '@/components/profile/FollowingListDialog';
import { SettingsModal } from '@/components/profile/SettingsModal';


const Profile = () => {
  const isMobile = useIsMobile();
  const { user: authUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const { profile, loading, error, isOwnProfile } = useUserProfile(userId);
  const { getRelationshipStatus, followUser } = useFriends();
  const { tournamentsCount } = useUserTournamentsCount(userId);
  const { followersCount } = useUserFollowersCount(userId);
  const { myEvents } = useMyEvents(userId);
  const { followingCount, refetch: refetchFollowingCount } = useUserFollowingCount(userId || authUser?.id);
  
  // Force refetch following count when component mounts or userId changes
  useEffect(() => {
    if (authUser && (userId || authUser.id)) {
      const timer = setTimeout(() => {
        refetchFollowingCount();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userId, authUser?.id, refetchFollowingCount]);
  
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowersDialog, setShowFollowersDialog] = useState(false);
  const [showFollowingDialog, setShowFollowingDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [idCopied, setIdCopied] = useState(false);

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id.slice(0, 8));
      setIdCopied(true);
      toast.success('ID copiado al portapapeles');
      setTimeout(() => setIdCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar el ID');
    }
  };


  useEffect(() => {
    if (!authLoading && !authUser) {
      navigate('/auth');
    }
  }, [authUser, authLoading, navigate]);

  // Show loading while checking authentication or profile data
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Cargando...</div>
      </div>
    );
  }

  // Redirect unauthenticated users immediately - don't render anything
  if (!authUser) {
    return null;
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Perfil no encontrado</h2>
          <p className="text-gray-600">Este usuario no existe o no está disponible.</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const favoriteSports = profile.preferred_sports || ['Tenis', 'Pádel', 'Pickleball'];
  const userLevel = profile.skill_level || 'Intermedio';
  const memberSince = new Date(profile.created_at).getFullYear().toString();

  // Función para obtener el nombre del usuario con múltiples fallbacks
  const getDisplayName = () => {
    // 1. Priorizar full_name del perfil en BD
    if (profile?.full_name) {
      return profile.full_name;
    }
    
    // 2. Fallback a metadata del registro
    if (authUser?.user_metadata?.full_name) {
      return authUser.user_metadata.full_name as string;
    }
    
    // 3. Usar parte del email como nombre
    if (authUser?.email) {
      const emailName = authUser.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return "Mi Perfil";
  };

  // Obtener iniciales para el avatar
  const getAvatarInitials = () => {
    const name = getDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  const relationshipStatus = isOwnProfile ? null : getRelationshipStatus(profile.user_id);

  // Normalize sport names to avoid duplicates (Padel, padel, Pádel -> Pádel)
  const normalizeSportName = (sport: string): string => {
    const normalized = sport.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (normalized === 'padel') return 'Pádel';
    if (normalized === 'tenis') return 'Tenis';
    if (normalized === 'pickleball') return 'Pickleball';
    return sport;
  };

  // Obtener deportes únicos de los eventos registrados
  const getRegisteredSports = (): string[] => {
    const allEvents = [
      ...(myEvents?.upcoming || []),
      ...(myEvents?.active || []),
      ...(myEvents?.finished || [])
    ];
    const uniqueSports = [...new Set(allEvents.map(e => normalizeSportName(e.sport)))];
    return uniqueSports;
  };
  
  const registeredSports = isOwnProfile ? favoriteSports : getRegisteredSports();

  // Debug logging for following count
  console.log('Profile following count debug:', {
    followingCount,
    userId: userId || authUser?.id,
    profileUserId: profile.user_id
  });

  const handleFollowUser = async () => {
    if (isOwnProfile || !profile || followLoading) return;
    
    setFollowLoading(true);
    
    try {
      const result = await followUser(profile.user_id);
      if (result.error) {
        console.error('Error following user:', result.error);
      } else {
        // Refresh following count after successful follow
        setTimeout(() => {
          refetchFollowingCount();
        }, 200);
      }
    } catch (error) {
      console.error('Error following user:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleBackNavigation = () => {
    const returnTo = location.state?.returnTo;
    
    if (returnTo?.screen) {
      // Navigate back to the specific screen with its data
      navigate('/', { 
        state: { 
          currentScreen: returnTo.screen, 
          screenData: returnTo.data 
        } 
      });
    } else {
      // Fallback to default behavior
      const fromPath = location.state?.from;
      if (fromPath === '/explore-players') {
        navigate('/', { state: { activeTab: 'events', exploreTab: 'players' } });
      } else if (fromPath === '/notifications') {
        navigate('/', { state: { currentScreen: 'notifications', screenData: { mode: 'notifications' } } });
      } else {
        navigate(-1);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header with user info */}
      <div className="bg-card shadow-sm border-b">
        <div className="container mx-auto px-3 py-4 md:px-4 md:py-6" 
             style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 1rem))' }}>
          {/* Back Button for other users' profiles */}
          {!isOwnProfile && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleBackNavigation}
              className="flex items-center gap-1.5 mb-3 -ml-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
          )}

          {isMobile && !isOwnProfile ? (
            /* MOBILE LAYOUT FOR OTHER USERS */
            <div className="space-y-3">
              {/* Avatar + Info Row */}
              <div className="flex items-start gap-3">
                <Avatar className="h-16 w-16 border-2 border-border shadow-md shrink-0">
                  <AvatarImage src={profile?.avatar_url || "/placeholder.svg"} alt={getDisplayName()} />
                  <AvatarFallback className="text-lg font-bold bg-gradient-to-br from-primary to-primary/70 text-primary-foreground">
                    {getAvatarInitials()}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-bold text-foreground truncate">
                    {getDisplayName()}
                  </h1>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCopyId(profile.user_id); }}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="font-mono">ID: {profile.user_id.slice(0, 8)}</span>
                    {idCopied ? <Check className="h-2.5 w-2.5 text-green-500" /> : <Copy className="h-2.5 w-2.5" />}
                  </button>
                  
                  {/* Location */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{profile.location || 'Sin ubicación'}</span>
                  </div>
                  
                  {/* Sports badges */}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {registeredSports.slice(0, 3).map(sport => (
                      <Badge key={sport} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {sport}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Member since */}
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
                    <Calendar className="h-2.5 w-2.5" />
                    <span>Miembro desde {memberSince}</span>
                  </div>
                </div>
              </div>

              {/* Compact Stats Row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <div className="text-lg font-bold text-foreground">{tournamentsCount || 0}</div>
                  <div className="text-[10px] text-muted-foreground">Torneos</div>
                </div>
                <button
                  onClick={() => setShowFollowersDialog(true)}
                  className="text-center p-2 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="text-lg font-bold text-foreground">{followersCount || 0}</div>
                  <div className="text-[10px] text-muted-foreground">Seguidores</div>
                </button>
                <button
                  onClick={() => setShowFollowingDialog(true)}
                  className="text-center p-2 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="text-lg font-bold text-foreground">{followingCount || 0}</div>
                  <div className="text-[10px] text-muted-foreground">Siguiendo</div>
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {relationshipStatus === 'mutual_following' ? (
                  <Button variant="secondary" size="sm" className="flex-1" disabled>
                    <Users className="h-4 w-4 mr-1" />
                    Amigos
                  </Button>
                ) : relationshipStatus === 'i_follow_them' ? (
                  <Button variant="secondary" size="sm" className="flex-1" disabled>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Siguiendo
                  </Button>
                ) : (
                  <Button onClick={handleFollowUser} size="sm" className="flex-1" disabled={followLoading}>
                    <UserPlus className="h-4 w-4 mr-1" />
                    {followLoading ? 'Siguiendo...' : relationshipStatus === 'they_follow_me' ? 'Seguir' : 'Seguir'}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            /* DESKTOP LAYOUT OR OWN PROFILE */
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl md:text-2xl font-bold text-foreground">
                      {getDisplayName()}
                    </h1>
                    <button
                      onClick={() => handleCopyId(profile.user_id)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-0.5"
                    >
                      <span className="font-mono">ID: {profile.user_id.slice(0, 8)}</span>
                      {idCopied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                    </button>
                  </div>
                  {isOwnProfile ? (
                    <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Configuración
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      {relationshipStatus === 'mutual_following' ? (
                        <Button variant="secondary" size="sm" disabled>
                          <Users className="h-4 w-4 mr-2" />
                          Amigos
                        </Button>
                      ) : relationshipStatus === 'i_follow_them' ? (
                        <Button variant="secondary" size="sm" disabled>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Siguiendo
                        </Button>
                      ) : (
                        <Button onClick={handleFollowUser} size="sm" disabled={followLoading}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          {followLoading ? 'Siguiendo...' : relationshipStatus === 'they_follow_me' ? 'Seguir de vuelta' : 'Seguir'}
                        </Button>
                      )}
                      <Button variant="outline" size="sm">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Mensaje
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* User Profile Header */}
              <div className="flex items-start space-x-6">
                <div className="relative">
                  <Avatar className="h-20 w-20 md:h-24 md:w-24 border-4 border-background shadow-lg">
                    <AvatarImage src={profile?.avatar_url || "/placeholder.svg"} alt={getDisplayName()} />
                    <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-primary to-primary/70 text-primary-foreground">
                      {getAvatarInitials()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                <div className="flex-1">
                  {/* Info for desktop */}
                  {!isOwnProfile && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{profile.location || 'Sin ubicación'}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {registeredSports.map(sport => (
                          <Badge key={sport} variant="secondary" className="text-xs">
                            {sport}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <div className="text-2xl font-bold text-foreground">{tournamentsCount || 0}</div>
                      <div className="text-xs text-muted-foreground">Torneos</div>
                    </div>
                    
                    <button
                      onClick={() => setShowFollowersDialog(true)}
                      className="text-center p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="text-2xl font-bold text-foreground">{followersCount || 0}</div>
                      <div className="text-xs text-muted-foreground">Seguidores</div>
                    </button>
                    
                    <button
                      onClick={() => setShowFollowingDialog(true)}
                      className="text-center p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="text-2xl font-bold text-foreground">{followingCount || 0}</div>
                      <div className="text-xs text-muted-foreground">Siguiendo</div>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Solo mostrar estos componentes para el propio perfil */}
        {isOwnProfile && (
          <>
            <RecentActivity userId={userId} />
            <FriendsNetwork userId={userId} />
          </>
        )}
        
        {/* Eventos siempre visibles */}
        <MyEvents userId={userId} isOwnProfile={isOwnProfile} />

        {/* Bio section */}
        {profile.bio && (
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Sobre {profile.full_name?.split(' ')[0] || 'este usuario'}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{profile.bio}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialogs */}
      <FollowersListDialog 
        open={showFollowersDialog} 
        onOpenChange={setShowFollowersDialog}
        userId={userId}
      />
      
      <FollowingListDialog 
        open={showFollowingDialog} 
        onOpenChange={setShowFollowingDialog}
        userId={userId}
        onFollowingChanged={refetchFollowingCount}
      />

      {isOwnProfile && (
        <>
          <SettingsModal open={showSettings} onOpenChange={setShowSettings} />
        </>
      )}
    </div>
  );
};

export default Profile;
